import { useCallback, useEffect, useMemo, useState } from "preact/hooks";

import {
  createProjectTask,
  deleteProjectTask,
  fetchProjectTasks,
  patchProjectTask,
} from "../lib/api";
import type { ProjectTaskOut } from "../types";

// Sprint-017 E7 (ADR-003 §5, design §3) — the editable checklist body's
// data layer for ONE project. Fetches `GET /projects/{id}/tasks`, keeps the
// live (non-archived) rows sorted by `position`, and exposes the write
// actions the checklist needs: add / toggle-done / rename / reorder /
// assign / delete. All optimistic where the design calls for it (the
// tap-to-check is optimistic-with-no-snackbar, matching the chore toggle),
// reverting on failure. `error` surfaces the existing `error-hint` copy.

function byPosition(a: ProjectTaskOut, b: ProjectTaskOut): number {
  if (a.position !== b.position) return a.position - b.position;
  return a.created_at.localeCompare(b.created_at);
}

export interface UseProjectTasksResult {
  tasks: ProjectTaskOut[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addTask: (title: string, safety?: boolean) => Promise<void>;
  toggleDone: (taskId: string, done: boolean) => Promise<boolean>;
  renameTask: (taskId: string, title: string) => Promise<void>;
  reorderTask: (taskId: string, newIndex: number) => Promise<void>;
  assignTask: (taskId: string, assigneeId: string | null) => Promise<void>;
  setSafety: (taskId: string, safety: boolean) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
}

export function useProjectTasks(projectId: string, enabled = true): UseProjectTasksResult {
  const [rows, setRows] = useState<ProjectTaskOut[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const fetched = await fetchProjectTasks(projectId);
      setRows(fetched.filter((t) => t.archived_at == null));
    } catch {
      setError("Couldn't load the list.");
    } finally {
      setLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upsert = useCallback((task: ProjectTaskOut) => {
    setRows((prev) => {
      const idx = prev.findIndex((t) => t.id === task.id);
      if (idx === -1) return [...prev, task];
      const next = [...prev];
      next[idx] = task;
      return next;
    });
  }, []);

  const addTask = useCallback(
    async (title: string, safety = false) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      const created = await createProjectTask(projectId, {
        idempotency_key: crypto.randomUUID(),
        title: trimmed,
        safety,
      });
      upsert(created);
    },
    [projectId, upsert],
  );

  const toggleDone = useCallback(
    async (taskId: string, done: boolean): Promise<boolean> => {
      // Optimistic — reflect the new state instantly (no snackbar), revert
      // on failure and let the caller surface the shared save error.
      setRows((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, done, completed_at: done ? new Date().toISOString() : null }
            : t,
        ),
      );
      try {
        const updated = await patchProjectTask(projectId, taskId, {
          idempotency_key: crypto.randomUUID(),
          done,
        });
        upsert(updated);
        return true;
      } catch {
        setRows((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? { ...t, done: !done, completed_at: !done ? new Date().toISOString() : null }
              : t,
          ),
        );
        return false;
      }
    },
    [projectId, upsert],
  );

  const renameTask = useCallback(
    async (taskId: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      const updated = await patchProjectTask(projectId, taskId, {
        idempotency_key: crypto.randomUUID(),
        title: trimmed,
      });
      upsert(updated);
    },
    [projectId, upsert],
  );

  const assignTask = useCallback(
    async (taskId: string, assigneeId: string | null) => {
      const updated = await patchProjectTask(projectId, taskId, {
        idempotency_key: crypto.randomUUID(),
        assignee_id: assigneeId,
      });
      upsert(updated);
    },
    [projectId, upsert],
  );

  const setSafety = useCallback(
    async (taskId: string, safety: boolean) => {
      // Optimistic — mark/clear immediately; revert on failure.
      setRows((prev) => prev.map((t) => (t.id === taskId ? { ...t, safety } : t)));
      try {
        const updated = await patchProjectTask(projectId, taskId, {
          idempotency_key: crypto.randomUUID(),
          safety,
        });
        upsert(updated);
      } catch {
        setRows((prev) => prev.map((t) => (t.id === taskId ? { ...t, safety: !safety } : t)));
      }
    },
    [projectId, upsert],
  );

  const reorderTask = useCallback(
    async (taskId: string, newIndex: number) => {
      const ordered = [...rows].sort(byPosition);
      const from = ordered.findIndex((t) => t.id === taskId);
      if (from === -1) return;
      const clamped = Math.max(0, Math.min(newIndex, ordered.length - 1));
      if (clamped === from) return;
      const [moved] = ordered.splice(from, 1);
      ordered.splice(clamped, 0, moved as ProjectTaskOut);
      // Optimistic reindex 0..n so the UI settles immediately.
      const reindexed = ordered.map((t, i) => ({ ...t, position: i }));
      setRows(reindexed);
      try {
        const updated = await patchProjectTask(projectId, taskId, {
          idempotency_key: crypto.randomUUID(),
          position: clamped,
        });
        upsert(updated);
      } catch {
        await refresh();
      }
    },
    [projectId, rows, upsert, refresh],
  );

  const deleteTask = useCallback(
    async (taskId: string) => {
      setRows((prev) => prev.filter((t) => t.id !== taskId));
      try {
        await deleteProjectTask(projectId, taskId, crypto.randomUUID());
      } catch {
        await refresh();
      }
    },
    [projectId, refresh],
  );

  const tasks = useMemo(() => [...rows].sort(byPosition), [rows]);

  return {
    tasks,
    loading,
    error,
    refresh,
    addTask,
    toggleDone,
    renameTask,
    reorderTask,
    assignTask,
    setSafety,
    deleteTask,
  };
}
